import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Search, 
  Globe, 
  BarChart3, 
  FileText, 
  Code,
  Save,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSEOSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('seo');
  const [site, setSite] = useState(null);
  
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_title_es: '',
    meta_description: '',
    meta_description_es: '',
    meta_keywords: '',
    meta_keywords_es: '',
    og_image: '',
    twitter_handle: '',
    google_site_verification: '',
    bing_site_verification: ''
  });

  const [analyticsData, setAnalyticsData] = useState({
    google_analytics_id: '',
    google_tag_manager_id: '',
    facebook_pixel_id: '',
    hotjar_id: '',
    custom_head_code: '',
    custom_body_code: ''
  });

  const [legalData, setLegalData] = useState({
    privacy_policy: '',
    privacy_policy_es: '',
    terms_of_service: '',
    terms_of_service_es: '',
    cookie_policy: '',
    cookie_policy_es: '',
    show_cookie_banner: true,
    cookie_banner_text: 'We use cookies to enhance your experience.',
    cookie_banner_text_es: 'Usamos cookies para mejorar tu experiencia.'
  });

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/site/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSite(data);
        
        if (data.seo) {
          setSeoData(prev => ({ ...prev, ...data.seo }));
        }
        if (data.analytics) {
          setAnalyticsData(prev => ({ ...prev, ...data.analytics }));
        }
        if (data.legal) {
          setLegalData(prev => ({ ...prev, ...data.legal }));
        }
      }
    } catch (error) {
      toast.error('Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        seo: seoData,
        analytics: analyticsData,
        legal: legalData
      };

      const response = await fetch(`${API_URL}/api/admin/site/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Settings saved!');
      } else {
        toast.error('Error saving settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'seo', name: 'SEO', icon: Search },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'legal', name: 'Legal Pages', icon: FileText },
    { id: 'code', name: 'Custom Code', icon: Code }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-terracota" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-seo-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">SEO & Settings</h1>
          <p className="text-stone-500">Manage SEO, analytics, and legal pages</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-terracota text-white px-6 py-2.5 rounded-full font-medium hover:bg-stone-900 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Changes
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-terracota text-terracota'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* SEO Tab */}
      {activeTab === 'seo' && (
        <div className="bg-white rounded-2xl p-6 border border-stone-200 space-y-6">
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
            <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Search Engine Optimization</h3>
              <p className="text-sm text-blue-700">These settings help your site rank better in Google and other search engines.</p>
            </div>
          </div>

          {/* Meta Title */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Meta Title (EN)
              </label>
              <input
                type="text"
                value={seoData.meta_title || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_title: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="Margaret Núñez | Founder & Creative Director"
                maxLength={60}
              />
              <p className="text-xs text-stone-400 mt-1">{(seoData.meta_title || '').length}/60 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Meta Title (ES)
              </label>
              <input
                type="text"
                value={seoData.meta_title_es || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_title_es: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="Margaret Núñez | Fundadora y Directora Creativa"
                maxLength={60}
              />
              <p className="text-xs text-stone-400 mt-1">{(seoData.meta_title_es || '').length}/60 characters</p>
            </div>
          </div>

          {/* Meta Description */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Meta Description (EN)
              </label>
              <textarea
                value={seoData.meta_description || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_description: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="Creative direction, brand identity, and experience design for luxury brands..."
                rows={3}
                maxLength={160}
              />
              <p className="text-xs text-stone-400 mt-1">{(seoData.meta_description || '').length}/160 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Meta Description (ES)
              </label>
              <textarea
                value={seoData.meta_description_es || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_description_es: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="Dirección creativa, identidad de marca y diseño de experiencias para marcas de lujo..."
                rows={3}
                maxLength={160}
              />
              <p className="text-xs text-stone-400 mt-1">{(seoData.meta_description_es || '').length}/160 characters</p>
            </div>
          </div>

          {/* Keywords */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Keywords (EN)
              </label>
              <input
                type="text"
                value={seoData.meta_keywords || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_keywords: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="creative director, brand identity, luxury events, hospitality"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Keywords (ES)
              </label>
              <input
                type="text"
                value={seoData.meta_keywords_es || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_keywords_es: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="directora creativa, identidad de marca, eventos de lujo, hospitalidad"
              />
            </div>
          </div>

          {/* Social Sharing */}
          <div className="pt-4 border-t border-stone-200">
            <h3 className="font-medium text-stone-900 mb-4">Social Sharing</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Open Graph Image URL
                </label>
                <input
                  type="url"
                  value={seoData.og_image || ''}
                  onChange={(e) => setSeoData({ ...seoData, og_image: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="https://..."
                />
                <p className="text-xs text-stone-400 mt-1">Recommended: 1200x630px</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Twitter Handle
                </label>
                <input
                  type="text"
                  value={seoData.twitter_handle || ''}
                  onChange={(e) => setSeoData({ ...seoData, twitter_handle: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="@margaretnunez"
                />
              </div>
            </div>
          </div>

          {/* Verification */}
          <div className="pt-4 border-t border-stone-200">
            <h3 className="font-medium text-stone-900 mb-4">Site Verification</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Google Search Console
                </label>
                <input
                  type="text"
                  value={seoData.google_site_verification || ''}
                  onChange={(e) => setSeoData({ ...seoData, google_site_verification: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="Verification code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Bing Webmaster
                </label>
                <input
                  type="text"
                  value={seoData.bing_site_verification || ''}
                  onChange={(e) => setSeoData({ ...seoData, bing_site_verification: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="Verification code"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="bg-white rounded-2xl p-6 border border-stone-200 space-y-6">
          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl">
            <BarChart3 className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-green-900">Analytics & Tracking</h3>
              <p className="text-sm text-green-700">Connect your analytics tools to track visitors and conversions.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Google Analytics 4 ID
              </label>
              <input
                type="text"
                value={analyticsData.google_analytics_id || ''}
                onChange={(e) => setAnalyticsData({ ...analyticsData, google_analytics_id: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="G-XXXXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Google Tag Manager ID
              </label>
              <input
                type="text"
                value={analyticsData.google_tag_manager_id || ''}
                onChange={(e) => setAnalyticsData({ ...analyticsData, google_tag_manager_id: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="GTM-XXXXXXX"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Facebook Pixel ID
              </label>
              <input
                type="text"
                value={analyticsData.facebook_pixel_id || ''}
                onChange={(e) => setAnalyticsData({ ...analyticsData, facebook_pixel_id: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="XXXXXXXXXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Hotjar Site ID
              </label>
              <input
                type="text"
                value={analyticsData.hotjar_id || ''}
                onChange={(e) => setAnalyticsData({ ...analyticsData, hotjar_id: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                placeholder="XXXXXXX"
              />
            </div>
          </div>
        </div>
      )}

      {/* Legal Tab */}
      {activeTab === 'legal' && (
        <div className="bg-white rounded-2xl p-6 border border-stone-200 space-y-6">
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl">
            <FileText className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-900">Legal Pages</h3>
              <p className="text-sm text-amber-700">Add your Privacy Policy, Terms of Service, and Cookie Policy.</p>
            </div>
          </div>

          {/* Cookie Banner */}
          <div className="p-4 bg-stone-50 rounded-xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={legalData.show_cookie_banner}
                onChange={(e) => setLegalData({ ...legalData, show_cookie_banner: e.target.checked })}
                className="w-5 h-5 rounded text-terracota"
              />
              <div>
                <span className="font-medium text-stone-900">Show Cookie Banner</span>
                <p className="text-sm text-stone-500">Display a cookie consent banner to visitors</p>
              </div>
            </label>
            
            {legalData.show_cookie_banner && (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={legalData.cookie_banner_text || ''}
                  onChange={(e) => setLegalData({ ...legalData, cookie_banner_text: e.target.value })}
                  className="px-4 py-2 border border-stone-300 rounded-lg text-sm"
                  placeholder="Cookie banner text (EN)"
                />
                <input
                  type="text"
                  value={legalData.cookie_banner_text_es || ''}
                  onChange={(e) => setLegalData({ ...legalData, cookie_banner_text_es: e.target.value })}
                  className="px-4 py-2 border border-stone-300 rounded-lg text-sm"
                  placeholder="Cookie banner text (ES)"
                />
              </div>
            )}
          </div>

          {/* Privacy Policy */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Privacy Policy (EN)
            </label>
            <textarea
              value={legalData.privacy_policy || ''}
              onChange={(e) => setLegalData({ ...legalData, privacy_policy: e.target.value })}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 font-mono text-sm"
              rows={8}
              placeholder="# Privacy Policy&#10;&#10;Last updated: [Date]&#10;&#10;..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Privacy Policy (ES)
            </label>
            <textarea
              value={legalData.privacy_policy_es || ''}
              onChange={(e) => setLegalData({ ...legalData, privacy_policy_es: e.target.value })}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 font-mono text-sm"
              rows={8}
              placeholder="# Política de Privacidad&#10;&#10;Última actualización: [Fecha]&#10;&#10;..."
            />
          </div>

          {/* Terms of Service */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Terms of Service (EN)
            </label>
            <textarea
              value={legalData.terms_of_service || ''}
              onChange={(e) => setLegalData({ ...legalData, terms_of_service: e.target.value })}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 font-mono text-sm"
              rows={8}
              placeholder="# Terms of Service&#10;&#10;Last updated: [Date]&#10;&#10;..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Terms of Service (ES)
            </label>
            <textarea
              value={legalData.terms_of_service_es || ''}
              onChange={(e) => setLegalData({ ...legalData, terms_of_service_es: e.target.value })}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 font-mono text-sm"
              rows={8}
              placeholder="# Términos de Servicio&#10;&#10;Última actualización: [Fecha]&#10;&#10;..."
            />
          </div>
        </div>
      )}

      {/* Custom Code Tab */}
      {activeTab === 'code' && (
        <div className="bg-white rounded-2xl p-6 border border-stone-200 space-y-6">
          <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-xl">
            <Code className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-purple-900">Custom Code Injection</h3>
              <p className="text-sm text-purple-700">Add custom HTML, CSS, or JavaScript to your site.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Warning</h3>
              <p className="text-sm text-red-700">Custom code can break your site. Only add code from trusted sources.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Header Code (inside &lt;head&gt;)
            </label>
            <textarea
              value={analyticsData.custom_head_code || ''}
              onChange={(e) => setAnalyticsData({ ...analyticsData, custom_head_code: e.target.value })}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 font-mono text-sm"
              rows={6}
              placeholder="<!-- Custom scripts, styles, or meta tags -->"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Footer Code (before &lt;/body&gt;)
            </label>
            <textarea
              value={analyticsData.custom_body_code || ''}
              onChange={(e) => setAnalyticsData({ ...analyticsData, custom_body_code: e.target.value })}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 font-mono text-sm"
              rows={6}
              placeholder="<!-- Chat widgets, analytics scripts, etc -->"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSEOSettings;
